import { useEffect, useRef, useState } from 'react';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { firebaseAuth, logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';

import { EMAIL_FOR_SIGN_IN_KEY } from './sendOnboardingLink';
import styles from './OnboardingLink.module.css';

/**
 * Atterrissage du magic-link envoyé par la passerelle mobile (/welcome).
 * Ouvert en principe sur un ORDINATEUR : on termine la connexion sans mot de
 * passe, puis direction la home — où la gate lancera le tutoriel si le compte
 * porte encore onboardingCompleted: false.
 *
 * Cross-device oblige (lien envoyé du téléphone, ouvert sur l'ordi),
 * l'email n'est pas dans le localStorage local : Firebase exige alors de le
 * confirmer — un champ, c'est aussi une protection contre les liens volés.
 */

type Status = 'signing' | 'needEmail' | 'invalid';

/** Origine du lien (?from=bridge | phone_home | animator_tv_help), pour la mesure. */
function linkSource() {
    return new URLSearchParams(window.location.search).get('from') || 'bridge';
}

export function OnboardingLink() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { auth } = useAuth();

    const [status, setStatus] = useState<Status>('signing');
    const [email, setEmail] = useState('');
    const [wrongEmail, setWrongEmail] = useState(false);
    const startedRef = useRef(false);

    const attempt = async (mail: string) => {
        setStatus('signing');
        setWrongEmail(false);
        try {
            await signInWithEmailLink(firebaseAuth, mail, window.location.href);
            try {
                localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
            } catch {
                /* localStorage indispo */
            }
            logEvent('onboarding_bridge_signin_success', undefined, { from: linkSource() });
            navigate('/home', { replace: true });
        } catch (e) {
            const code = (e as { code?: string }).code || '';
            // Mauvaise adresse saisie → on laisse retenter ; tout le reste
            // (lien expiré, déjà consommé, malformé) → état invalide.
            if (code === 'auth/invalid-email' || code === 'auth/user-mismatch') {
                setWrongEmail(true);
                setStatus('needEmail');
            } else {
                setStatus('invalid');
            }
        }
    };

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        // Le lien Firebase porte la langue de l'email (&lang=…) : on aligne la
        // page dessus plutôt que sur la langue du navigateur qui l'ouvre.
        const lang = new URLSearchParams(window.location.search).get('lang');
        if (lang) i18n.changeLanguage(lang.split('-')[0]);
        // Déjà connecté (lien re-cliqué après coup, ou re-render post sign-in
        // côté MainRoutes) : la home s'occupe du reste.
        if (auth) {
            navigate('/home', { replace: true });
            return;
        }
        if (!isSignInWithEmailLink(firebaseAuth, window.location.href)) {
            setStatus('invalid');
            return;
        }
        logEvent('onboarding_bridge_link_opened', undefined, { from: linkSource() });
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
        } catch {
            /* localStorage indispo */
        }
        if (stored) {
            attempt(stored);
        } else {
            setStatus('needEmail');
        }
    }, []);

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return (
        <div className={styles.page}>
            <img src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" className={styles.logo} />

            <main className={styles.content}>
                {status === 'signing' && (
                    <div className={styles.card}>
                        <div className={styles.spinner} />
                        <p className={styles.subtitle}>{t('onboarding.link.verifying')}</p>
                    </div>
                )}

                {status === 'needEmail' && (
                    <div className={styles.card}>
                        <h1 className={styles.title}>{t('onboarding.link.confirmTitle')}</h1>
                        <p className={styles.subtitle}>{t('onboarding.link.confirmSubtitle')}</p>
                        <input
                            className={styles.emailInput}
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('onboarding.bridge.emailPlaceholder')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && emailValid) attempt(email);
                            }}
                        />
                        {wrongEmail && <p className={styles.error}>{t('onboarding.link.wrongEmail')}</p>}
                        <button
                            type="button"
                            className={styles.cta}
                            onClick={() => attempt(email)}
                            disabled={!emailValid}
                        >
                            {t('onboarding.link.confirmButton')}
                        </button>
                    </div>
                )}

                {status === 'invalid' && (
                    <div className={styles.card}>
                        <div className={styles.glyph}>⏳</div>
                        <h1 className={styles.title}>{t('onboarding.link.invalidTitle')}</h1>
                        <p className={styles.subtitle}>{t('onboarding.link.invalidSubtitle')}</p>
                        <button
                            type="button"
                            className={styles.cta}
                            onClick={() => navigate('/signin', { replace: true })}
                        >
                            {t('onboarding.link.goSignin')}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}
